import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { Bike, MapPin, Phone, Navigation, Power, CheckCircle2, Package, Clock, Wallet, History, AlertOctagon, Flame, ChefHat, BookOpen } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatGNF, STATUS_LABEL, STATUS_COLOR, operatorLabel } from "@/utils/format";
import { toast } from "sonner";
import PushPermissionBanner from "@/components/PushPermissionBanner";
import SilentModeBanner from "@/components/SilentModeBanner";
import FullScreenAlertBanner from "@/components/FullScreenAlertBanner";
import DeliveryCodeModal from "@/components/DeliveryCodeModal";
import IncidentReportModal from "@/components/IncidentReportModal";
import DriverBalanceCard from "@/components/dashboard/DriverBalanceCard";
import DriverMissionMap from "@/components/driver/DriverMissionMap";
import ProfitabilityHeatmap from "@/components/dashboard/ProfitabilityHeatmap";
import { unlockAudio, playAlertSound, vibrate } from "@/lib/pushNotifications";

const REFRESH_MS = 15000;
const GPS_PUSH_MS = 10000;

export default function Driver() {
  const { t } = useTranslation();
  const { user, refresh: refreshUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [online, setOnline] = useState(!!user?.available);
  // Keep the local toggle in sync with the server (drift can hide the order pool)
  useEffect(() => {
    if (user && user.available != null) setOnline(!!user.available);
  }, [user?.available]); // eslint-disable-line react-hooks/exhaustive-deps
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [lastPos, setLastPos] = useState(null);
  const knownOfferIdsRef = useRef(null); // offres déjà vues (alerte son/vibration)
  const [pushedAt, setPushedAt] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [searchParams] = useSearchParams();
  const VALID_DRIVER_TABS = ["active", "history", "earnings", "heatmap"];
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(VALID_DRIVER_TABS.includes(urlTab) ? urlTab : "active"); // active | history | earnings
  // Bottom nav / notification deep-links: ?tab=... (+ focus=current → scroll to active course)
  useEffect(() => {
    const tp = searchParams.get("tab");
    if (tp && VALID_DRIVER_TABS.includes(tp)) setTab(tp);
    if (searchParams.get("focus") === "current") {
      setTimeout(() => document.getElementById("driver-current")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
    }
    // ?highlight=driver-balance → scroll to and flash the balance card (debt warnings)
    if (searchParams.get("highlight") === "driver-balance") {
      setTimeout(() => {
        const el = document.getElementById("driver-balance");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          el.classList.add("deep-link-highlight");
          setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
        }
      }, 350);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  const [history, setHistory] = useState({ items: [], total: 0 });
  const [stats, setStats] = useState(null);
  const share = stats?.share || 0.7; // part livreur — réglable par l'admin (renvoyée par /driver/stats)
  const [payouts, setPayouts] = useState([]);
  const [statsDays, setStatsDays] = useState(30);
  const [codeModal, setCodeModal] = useState(null); // order_id or null
  const [incidentModal, setIncidentModal] = useState(null); // order_id or null
  const [dismissedOffers, setDismissedOffers] = useState(new Set()); // "Passer" tapped
  const [showOthers, setShowOthers] = useState(false); // reveal pool during an active course
  const [gainsToday, setGainsToday] = useState(null); // { earnings, deliveries }

  // Gains du jour — refreshed every 60 s
  useEffect(() => {
    const load = () =>
      apiClient.get("/dashboard/driver?days=1")
        .then(({ data }) => setGainsToday(data?.kpis || null))
        .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  const watchIdRef = useRef(null);
  const pushTimerRef = useRef(null);
  const lastPosRef = useRef(null);
  const seenOffersRef = useRef(new Set());
  const seenPoolRef = useRef(new Set());

  const fetchOrders = useCallback(() => {
    apiClient.get("/driver/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  const fetchHistory = useCallback(() => {
    apiClient.get("/driver/history?limit=30").then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const fetchStats = useCallback(() => {
    apiClient.get(`/driver/stats?days=${statsDays}`).then(({ data }) => setStats(data)).catch(() => {});
  }, [statsDays]);

  const fetchPayouts = useCallback(() => {
    apiClient.get("/my/payouts").then(({ data }) => setPayouts(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "driver" || user?.role === "admin") {
      fetchOrders();
      const t = setInterval(fetchOrders, REFRESH_MS);
      return () => clearInterval(t);
    }
  }, [user, fetchOrders]);

  useEffect(() => {
    if ((user?.role === "driver" || user?.role === "admin") && tab === "history") fetchHistory();
    if ((user?.role === "driver" || user?.role === "admin") && tab === "earnings") { fetchStats(); fetchPayouts(); }
  }, [user, tab, fetchHistory, fetchStats, fetchPayouts]);

  // In-app alert layer: sound + vibration when a NEW priority offer / pool order appears
  useEffect(() => {
    const offers = orders.filter((o) => o.status === "ready" && o.is_priority_offer).map((o) => o.order_id);
    const pool = orders.filter((o) => o.status === "ready" && !o.is_priority_offer && !o.blocked_by_debt).map((o) => o.order_id);
    const newOffers = offers.filter((id) => !seenOffersRef.current.has(id));
    const newPool = pool.filter((id) => !seenPoolRef.current.has(id) && !seenOffersRef.current.has(id));
    if (newOffers.length > 0) {
      playAlertSound("priority");
      vibrate([300, 100, 300, 100, 300]);
      toast.warning(t("ddrv.toastPrio"), { duration: 8000 });
    } else if (newPool.length > 0) {
      playAlertSound("pool");
      vibrate([200, 100, 200]);
      toast.info(t("ddrv.toastNewOrder"), { duration: 5000 });
    }
    offers.forEach((id) => seenOffersRef.current.add(id));
    pool.forEach((id) => seenPoolRef.current.add(id));
  }, [orders]);

  // Active delivery (delivering status)
  const activeDelivery = orders.find((o) => o.status === "delivering");

  const pushLocation = useCallback(async () => {
    if (!activeDelivery || !lastPosRef.current) return;
    try {
      const { latitude, longitude, accuracy } = lastPosRef.current;
      await apiClient.post("/driver/location", {
        order_id: activeDelivery.order_id,
        lat: latitude,
        lng: longitude,
        accuracy,
      });
      setPushedAt(new Date());
    } catch (err) {
      // Silent — likely the order changed status
    }
  }, [activeDelivery]);

  // Start / stop watching GPS based on `online` toggle
  useEffect(() => {
    if (!online) {
      if (watchIdRef.current != null && "geolocation" in navigator) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (pushTimerRef.current) {
        clearInterval(pushTimerRef.current);
        pushTimerRef.current = null;
      }
      return;
    }

    if (!("geolocation" in navigator)) {
      setGpsError(t("ddrv.gpsUnsupported"));
      setOnline(false);
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        lastPosRef.current = pos.coords;
        setLastPos({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGpsError(null);
      },
      (err) => {
        setGpsError(err.message || t("ddrv.gpsFail"));
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    pushTimerRef.current = setInterval(pushLocation, GPS_PUSH_MS);
    // Immediate push so client sees the marker fast
    setTimeout(pushLocation, 1500);

    // Heartbeat every 30s → back-end knows this driver is nearby for auto-dispatch
    const beat = () => {
      const c = lastPosRef.current;
      if (!c) return;
      apiClient.post("/driver/heartbeat", {
        lat: c.latitude, lng: c.longitude, accuracy: c.accuracy,
      }).catch(() => {});
    };
    const beatTimer = setInterval(beat, 30000);
    setTimeout(beat, 2500); // send first heartbeat quickly

    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (pushTimerRef.current) clearInterval(pushTimerRef.current);
      clearInterval(beatTimer);
      watchIdRef.current = null;
      pushTimerRef.current = null;
    };
  }, [online, pushLocation]);

  // Sync local `online` state with the persistent `available` flag on the user.
  // Also toggles server-side visibility of the "pool" of unassigned orders.
  const toggleOnline = useCallback(async () => {
    const next = !online;
    unlockAudio(); // user gesture → unlock Web Audio for in-app alert sounds
    setTogglingOnline(true);
    try {
      await apiClient.patch("/my/partner-profile", { available: next });
      setOnline(next);
      refreshUser?.();
      fetchOrders();
      toast.success(next ? t("ddrv.toastOnline") : t("ddrv.toastOffline"));
    } catch (err) {
      toast.error(t("ddrv.toastAvailFail"));
    } finally {
      setTogglingOnline(false);
    }
  }, [online, fetchOrders, refreshUser]);

  // Son + vibration à l'arrivée d'une nouvelle offre de course (AudioContext
  // partagé, déverrouillé au 1er geste — un contexte neuf reste muet sur iOS)
  useEffect(() => {
    const unlock = () => unlockAudio();
    document.addEventListener("pointerdown", unlock, { once: true, passive: true });
    document.addEventListener("touchend", unlock, { once: true, passive: true });
    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("touchend", unlock);
    };
  }, []);

  useEffect(() => {
    const ids = orders.filter((o) => o.status === "ready").map((o) => o.order_id);
    if (knownOfferIdsRef.current === null) {
      knownOfferIdsRef.current = new Set(ids); // premier chargement : pas d'alerte
      return;
    }
    const fresh = ids.filter((id) => !knownOfferIdsRef.current.has(id));
    ids.forEach((id) => knownOfferIdsRef.current.add(id));
    if (fresh.length > 0 && online) {
      vibrate([300, 120, 300]);
      playAlertSound("priority");
    }
  }, [orders, online]);

  // Alarme RÉPÉTÉE (toutes les 6 s) tant qu'une offre prioritaire reste affichée
  // sans réponse (acceptée / passée / expirée) et qu'aucune course n'est en cours.
  const pendingPriority = online
    && !orders.some((o) => o.status === "delivering")
    && orders.some((o) => o.status === "ready" && o.is_priority_offer && !dismissedOffers.has(o.order_id));
  useEffect(() => {
    if (!pendingPriority) return undefined;
    const timer = setInterval(() => {
      playAlertSound("priority");
      vibrate([300, 100, 300]);
    }, 6000);
    return () => clearInterval(timer);
  }, [pendingPriority]);

  if (!user || (user.role !== "driver" && user.role !== "admin")) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">{t("ddrv.driversOnly")}</div>;
  }

  const setStatus = async (orderId, status) => {
    // Delivered requires the 4-digit code — open modal instead of direct PATCH
    if (status === "delivered") {
      setCodeModal(orderId);
      return;
    }
    try {
      await apiClient.patch(`/orders/${orderId}/status`, { status });
      toast.success(t("ddrv.toastPicked"));
      fetchOrders();
      if (status === "delivering" && !online) setOnline(true);
    } catch (e) {
      const msg = e?.response?.status === 409
        ? t("ddrv.toastTooLate")
        : (e?.response?.data?.detail || t("ddrv.toastUpdateFail"));
      toast.error(msg);
      fetchOrders();
    }
  };

  const openIncident = (orderId) => setIncidentModal(orderId);

  const declineOffer = async (orderId) => {
    setDismissedOffers((s) => new Set([...s, orderId]));
    try {
      await apiClient.post(`/orders/${orderId}/decline-offer`);
      toast.info(t("ddrv.toastDeclined"));
    } catch { /* already expired/claimed */ }
    fetchOrders();
  };

  const readyPool = orders.filter((o) => o.status === "ready");
  const preannounce = orders.filter((o) => o.status === "preparing" && o.is_preannounce);
  const delivering = orders.filter((o) => o.status === "delivering");
  // Une commande = UNE seule représentation à l'écran : ce que la carte /
  // bottom sheet affiche n'est jamais répété dans les listes en dessous.
  const missionOrder = delivering[0] || null; // suivie dans le bottom sheet
  const sheetOffer = !missionOrder ? readyPool.find((o) => !o.is_priority_offer && !o.blocked_by_debt) : null;
  const deliveringList = delivering.filter((o) => o.order_id !== missionOrder?.order_id);
  const priorityOffers = readyPool.filter((o) => o.is_priority_offer);
  const openPool = readyPool.filter((o) => !o.is_priority_offer && o.order_id !== sheetOffer?.order_id);
  const hasActiveCourse = delivering.length > 0;
  // Full-screen offer: only when NOT already on a course, and not dismissed
  const overlayOffer = !hasActiveCourse ? priorityOffers.find((o) => !dismissedOffers.has(o.order_id)) : null;
  const otherCount = priorityOffers.length + openPool.length + preannounce.length;

  return (
    <div className="min-h-screen bg-sand pb-24">
      {/* Rappel si les notifications push sont désactivées (courses manquées) */}
      <div className="max-w-3xl mx-auto px-4 pt-3">
        <PushPermissionBanner />
        <SilentModeBanner />
        <FullScreenAlertBanner />
      </div>
      {/* STICKY HEADER */}
      <div className="sticky top-0 z-30 bg-white border-b border-sand shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-red font-semibold">{t("ddrv.headerKicker")}</div>
            <div className="font-display text-lg font-semibold leading-tight flex items-center gap-2">
              <Bike className="w-5 h-5 text-brand-red" /> {user.name || t("ddrv.headerKicker")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/driver/regles"
              data-testid="driver-rules-link"
              title={t("ddrv.rulesTitleAttr")}
              className="rounded-full p-2.5 bg-elevated text-ink hover:text-brand-red"
            >
              <BookOpen className="w-4 h-4" />
            </Link>
            <button
              onClick={toggleOnline}
              disabled={togglingOnline}
              data-testid="driver-online-toggle"
              className={`rounded-full px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition-all disabled:opacity-60 ${online ? "bg-brand-green text-white" : "bg-elevated text-ink"}`}
            >
              <Power className="w-4 h-4" />
              {online ? t("ddrv.online") : t("ddrv.offline")}
            </button>
          </div>
        </div>

        {/* GPS Status bar */}
        {online && (
          <div className="max-w-3xl mx-auto px-4 pb-3">
            <div className="rounded-xl bg-elevated px-3 py-2 text-xs flex items-center justify-between gap-2" data-testid="driver-gps-status">
              <span className="inline-flex items-center gap-2 text-mute">
                <Navigation className={`w-3.5 h-3.5 ${lastPos ? "text-brand-green" : "text-mute"}`} />
                {lastPos ? (
                  <>
                    {t("ddrv.gpsPosition")} : {lastPos.lat.toFixed(5)}, {lastPos.lng.toFixed(5)}
                    {lastPos.accuracy ? <span className="text-mute"> · ±{Math.round(lastPos.accuracy)}m</span> : null}
                  </>
                ) : gpsError ? (
                  <span className="text-brand-red">{gpsError}</span>
                ) : (
                  <span>{t("ddrv.gpsAcquiring")}</span>
                )}
              </span>
              {pushedAt && activeDelivery && (
                <span className="text-mute shrink-0">↑ {pushedAt.toLocaleTimeString("fr-FR")}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className="max-w-3xl mx-auto px-4 py-4 space-y-6">
        {/* Real-time balance card */}
        <div id="driver-balance" className="rounded-2xl">
          <DriverBalanceCard refreshKey={orders.length} />
        </div>

        {/* TABS */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="driver-tabs">
          {[
            { k: "active", label: t("rdash.ddTabActive"), icon: <Bike className="w-3.5 h-3.5" /> },
            { k: "history", label: t("rdash.ddTabHistory"), icon: <History className="w-3.5 h-3.5" /> },
            { k: "earnings", label: t("rdash.ddTabEarnings"), icon: <Wallet className="w-3.5 h-3.5" /> },
            { k: "heatmap", label: t("rdash.ddTabHeatmap"), icon: <Flame className="w-3.5 h-3.5" /> },
          ].map((it) => (
            <button
              key={it.k}
              onClick={() => setTab(it.k)}
              data-testid={`driver-tab-${it.k}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${tab === it.k ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
            >
              {it.icon} {it.label}
            </button>
          ))}
        </div>

        {tab === "active" && (
          <>
        {/* CARTE MISSION — cœur de l'expérience livreur (états 1→6) */}
        <DriverMissionMap
          orders={orders}
          online={online}
          lastPos={lastPos}
          onAccept={(o) => setStatus(o.order_id, "delivering")}
          onDecline={(o) => declineOffer(o.order_id)}
          onDelivered={(o) => setCodeModal(o.order_id)}
          onIncident={openIncident}
          onArrivedResto={(o) => apiClient.post(`/driver/orders/${o.order_id}/arrived-restaurant`).catch(() => {})}
        />
        {/* HERO — big online toggle + today's earnings (masqué quand une course est active) */}
        {!activeDelivery && (
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="driver-hero">
          <button
            onClick={toggleOnline}
            disabled={togglingOnline}
            data-testid="driver-online-hero"
            className={`w-full rounded-2xl py-5 text-lg font-extrabold tracking-wide inline-flex items-center justify-center gap-3 transition-all active:scale-[0.99] disabled:opacity-60 ${online ? "bg-brand-green text-white shadow-lg shadow-brand-green/25" : "bg-ink text-white"}`}
          >
            <Power className="w-6 h-6" />
            {togglingOnline ? "…" : online ? t("ddrv.onlineBig") : t("ddrv.offlineBig")}
          </button>
          <div className="mt-3 flex items-center justify-between rounded-xl bg-elevated px-4 py-3" data-testid="driver-gains-today">
            <div className="text-xs text-mute font-semibold uppercase tracking-widest">{t("ddrv.gainsToday")}</div>
            <div className="text-right">
              <div className="font-display text-xl font-bold text-brand-green">{formatGNF(gainsToday?.earnings || 0)}</div>
              <div className="text-[11px] text-mute">{t("ddrv.deliveries", { count: gainsToday?.deliveries || 0 })}</div>
            </div>
          </div>
        </div>
        )}

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatPill label={t("ddrv.statPickup")} value={readyPool.length} color="#F4C430" testid="stat-pickup" />
          <StatPill label={t("ddrv.statDelivering")} value={delivering.length} color="#E32636" testid="stat-delivering" />
          <StatPill label={t("ddrv.statPreparing")} value={preannounce.length} color="#E67E22" testid="stat-preannounce" />
        </div>

        {/* IN-PROGRESS DELIVERY — la mission du bottom sheet n'est PAS répétée ici */}
        {deliveringList.length > 0 && (
          <section id="driver-current">
            <SectionTitle>{t("ddrv.secDelivering")}</SectionTitle>
            <div className="space-y-3">
              {deliveringList.map((o) => (
                <OrderCard key={o.order_id} order={o} primary onAction={setStatus} online={online} onIncident={openIncident} share={share} />
              ))}
            </div>
          </section>
        )}

        {/* Pendant une mission, les autres listes sont repliées derrière ce bouton */}
        {hasActiveCourse && otherCount > 0 && (
          <button
            onClick={() => setShowOthers((v) => !v)}
            data-testid="driver-show-others"
            className="w-full rounded-xl border border-sand bg-white py-2.5 text-xs font-semibold text-mute hover:text-ink"
          >
            {showOthers ? t("ddrv.hideOthers") : t("ddrv.showOthers", { n: otherCount })}
          </button>
        )}

        {(!hasActiveCourse || showOthers) && (
          <>
        {/* PRIORITY OFFERS — closest driver got exclusive access */}
        {priorityOffers.length > 0 && (
          <section data-testid="driver-priority-section">
            <div className="rounded-2xl bg-gradient-to-r from-brand-red to-[#C81F2D] text-white px-4 py-3 mb-3 flex items-center gap-2">
              <Flame className="w-4 h-4" />
              <div className="text-sm">
                <div className="font-display font-semibold">{t("ddrv.prioBannerTitle")}</div>
                <div className="text-xs opacity-90">{t("ddrv.prioBannerSub")}</div>
              </div>
            </div>
            <div className="space-y-3">
              {priorityOffers.map((o) => (
                <OrderCard key={o.order_id} order={o} priority onAction={setStatus} online={online} onIncident={openIncident} share={share} />
              ))}
            </div>
          </section>
        )}

        {/* READY-TO-PICK-UP (open pool, excludes priority offers already shown above) */}
        <section>
          <SectionTitle>{t("ddrv.secPickup")}</SectionTitle>
          {openPool.length === 0 ? (
            <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute text-sm">
              <Package className="w-6 h-6 mx-auto mb-2 opacity-50" />
              {t("ddrv.emptyPool")}
            </div>
          ) : (
            <div className="space-y-3">
              {openPool.map((o) => (
                <OrderCard key={o.order_id} order={o} onAction={setStatus} online={online} onIncident={openIncident} />
              ))}
            </div>
          )}
        </section>

        {/* PRE-ANNOUNCEMENTS — orders still being prepared (non-claimable) */}
        {preannounce.length > 0 && (
          <section data-testid="driver-preannounce-section">
            <SectionTitle>{t("ddrv.secPreannounce")}</SectionTitle>
            <div className="space-y-3">
              {preannounce.map((o) => (
                <PreannounceCard key={o.order_id} order={o} share={share} />
              ))}
            </div>
          </section>
        )}
          </>
        )}

        {/* Tip */}
        {!online && (
          <div className="bg-[#FFF5F6] border border-brand-red/20 rounded-2xl p-4 text-sm" data-testid="driver-offline-hint">
            <div className="font-semibold text-brand-red mb-1">{t("ddrv.offlineHintTitle")}</div>
            <p className="text-mute">{t("ddrv.offlineHintText")}</p>
          </div>
        )}
          </>
        )}

        {tab === "history" && <HistorySection history={history} highlight={searchParams.get("highlight")} share={share} />}
        {tab === "earnings" && <EarningsSection stats={stats} days={statsDays} setDays={setStatsDays} payouts={payouts} highlight={searchParams.get("highlight")} />}
        {tab === "heatmap" && <ProfitabilityHeatmap />}
      </div>

      <DeliveryCodeModal
        open={!!codeModal}
        orderId={codeModal}
        onClose={() => setCodeModal(null)}
        onVerified={() => { setCodeModal(null); fetchOrders(); }}
      />
      <IncidentReportModal
        open={!!incidentModal}
        order={orders.find((o) => o.order_id === incidentModal) || (incidentModal ? { order_id: incidentModal } : null)}
        onClose={() => setIncidentModal(null)}
        onSubmitted={() => { fetchOrders(); }}
      />

      {/* FULL-SCREEN PRIORITY OFFER — countdown + J'y vais / Passer */}
      {overlayOffer && (
        <PriorityOfferOverlay
          order={overlayOffer}
          share={share}
          onAccept={() => setStatus(overlayOffer.order_id, "delivering")}
          onDecline={() => declineOffer(overlayOffer.order_id)}
          onExpired={() => { setDismissedOffers((s) => new Set([...s, overlayOffer.order_id])); fetchOrders(); }}
        />
      )}
    </div>
  );
}

/** Full-screen exclusive offer (3 km / 60 s) — no scrolling, no searching. */
function PriorityOfferOverlay({ order: o, onAccept, onDecline, onExpired, share = 0.7 }) {
  const { t } = useTranslation();
  const [left, setLeft] = useState(() =>
    o.preferred_until ? Math.max(0, Math.floor((new Date(o.preferred_until).getTime() - Date.now()) / 1000)) : 60
  );
  useEffect(() => {
    const t = setInterval(() => {
      const s = o.preferred_until ? Math.max(0, Math.floor((new Date(o.preferred_until).getTime() - Date.now()) / 1000)) : 0;
      setLeft(s);
      if (s <= 0) { clearInterval(t); onExpired(); }
    }, 1000);
    return () => clearInterval(t);
  }, [o.preferred_until]); // eslint-disable-line react-hooks/exhaustive-deps

  const driverGain = Math.round((o.delivery_fee || 0) * share);
  return (
    <div className="fixed inset-0 z-[60] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="driver-offer-overlay">
      <div className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl">
        <div className="bg-gradient-to-r from-brand-red to-[#C81F2D] text-white px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-display text-lg font-bold flex items-center gap-2"><Flame className="w-5 h-5" /> {t("ddrv.overlayTitle")}</div>
            <div className="text-xs opacity-90">{t("ddrv.overlaySub")}</div>
          </div>
          <div className="text-center shrink-0" data-testid="offer-countdown">
            <div className={`font-display text-3xl font-extrabold tabular-nums ${left <= 15 ? "animate-pulse" : ""}`}>{left}s</div>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-xl font-bold">#{o.order_id.slice(-6).toUpperCase()}</div>
              <div className="text-sm font-semibold truncate">{o.restaurant_name}</div>
              <div className="text-xs text-mute flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {t("ddrv.pickupLabel")} : {o.restaurant_address || o.restaurant_zone || "—"}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-mute">{t("ddrv.yourGain")}</div>
              <div className="font-display text-xl font-bold text-brand-green">{formatGNF(driverGain)}</div>
              {o.dispatch_distance_km != null && <div className="text-[11px] text-mute">{t("ddrv.atKm", { km: o.dispatch_distance_km })}</div>}
            </div>
          </div>
          <div className="text-xs text-mute bg-elevated rounded-xl px-3 py-2">
            {o.items?.map((it) => `${it.quantity}× ${it.name}${it.options?.length ? ` (${it.options.map((op) => op.name).join(", ")})` : ""}`).join(" · ")}
            {o.payment_method === "cash" && <span className="font-bold text-[#8A6D00]"> · {t("ddrv.cashCollectInline")} {formatGNF(o.total)}</span>}
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              onClick={onDecline}
              data-testid="offer-decline"
              className="rounded-2xl border-2 border-sand hover:border-mute py-4 font-bold text-mute text-base active:scale-[0.98] transition-transform"
            >
              {t("ddrv.btnSkip")}
            </button>
            <button
              onClick={onAccept}
              data-testid="offer-accept"
              className="rounded-2xl bg-brand-green hover:bg-[#007A4A] text-white py-4 font-extrabold text-base inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Bike className="w-5 h-5" /> {t("ddrv.btnGo")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HistorySection({ history, highlight, share = 0.7 }) {
  const { t } = useTranslation();
  // Deep link from « Voir la commande » → scroll to and flash the order card
  useEffect(() => {
    if (!highlight || history.items.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`history-${highlight}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deep-link-highlight");
        setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlight, history.items]);
  return (
    <section data-testid="driver-history">
      <SectionTitle>{t("ddrv.histTitle")}</SectionTitle>
      {history.total === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-6 text-center text-mute text-sm">
          <History className="w-6 h-6 mx-auto mb-2 opacity-50" />
          {t("ddrv.histEmpty")}
        </div>
      ) : (
        <div className="space-y-2">
          {history.items.map((o) => (
            <div key={o.order_id} id={`history-${o.order_id}`} data-testid={`history-${o.order_id}`} className="bg-white border border-sand rounded-2xl p-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono text-xs text-mute">#{o.order_id.slice(-6).toUpperCase()}</span>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full`} style={{ backgroundColor: STATUS_COLOR[o.status] + "22", color: STATUS_COLOR[o.status] }}>{t(`status.${o.status}`, STATUS_LABEL[o.status])}</span>
                </div>
                <div className="font-semibold text-sm truncate">{o.restaurant_name}</div>
                <div className="text-xs text-mute truncate">{t("ddrv.histDeliveredTo")} {o.address} · {o.zone}</div>
                <div className="text-[11px] text-mute mt-1">{new Date(o.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-mute">{t("ddrv.yourGain")}</div>
                <div className="font-display text-base font-semibold text-brand-green" data-testid={`history-gain-${o.order_id}`}>{formatGNF(Math.round((o.delivery_fee || 0) * share))}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EarningsSection({ stats, days, setDays, payouts = [], highlight }) {
  const { t } = useTranslation();
  // Deep link (?highlight={payout_id}) → scroll to and flash the payout row
  useEffect(() => {
    if (!highlight || payouts.length === 0) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`payout-row-${highlight}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("deep-link-highlight");
        setTimeout(() => el.classList.remove("deep-link-highlight"), 3200);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlight, payouts]);

  if (!stats) return <div className="text-mute text-sm">…</div>;
  const tot = stats.totals;
  const sharePct = Math.round((stats?.share || 0.7) * 100);
  return (
    <section className="space-y-4" data-testid="driver-earnings">
      <div className="flex items-center justify-between">
        <SectionTitle>{t("ddrv.earnTitle", { pct: sharePct })}</SectionTitle>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`earnings-range-${d}`}
              className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border ${days === d ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
            >{t("ddrv.daysSuffix", { d })}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="earnings-total">
          <div className="text-xs uppercase tracking-widest text-brand-green flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> {t("ddrv.earnTotal")}</div>
          <div className="font-display text-2xl font-semibold mt-1">{formatGNF(tot.earnings)}</div>
          <div className="text-[11px] text-mute mt-1">{t("ddrv.earnTotalSub")}</div>
        </div>
        <div className="bg-white border border-sand rounded-2xl p-4" data-testid="earnings-deliveries">
          <div className="text-xs uppercase tracking-widest text-brand-red flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {t("ddrv.earnDeliveries")}</div>
          <div className="font-display text-2xl font-semibold mt-1">{tot.deliveries}</div>
          <div className="text-[11px] text-mute mt-1">
            {stats.active_deliveries > 0 ? t("ddrv.earnActive", { n: stats.active_deliveries }) : t("ddrv.earnNoneActive")}
          </div>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-2xl p-4">
        <div className="text-xs uppercase tracking-widest text-mute mb-2">{t("ddrv.earnDaily")}</div>
        {stats.series.length === 0 ? (
          <div className="text-mute text-sm py-4">{t("ddrv.earnEmptyPeriod")}</div>
        ) : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
            {stats.series.slice().reverse().map((d) => (
              <div key={d.date} className="flex items-center justify-between text-sm py-1 border-b border-sand/60 last:border-0" data-testid={`series-${d.date}`}>
                <span className="text-mute">{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}</span>
                <span className="text-mute text-xs">{d.deliveries} {t("ddrv.livAbbr")}</span>
                <span className="font-semibold text-brand-green">{formatGNF(d.earnings)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-sand rounded-2xl p-4" data-testid="driver-payouts">
        <div className="text-xs uppercase tracking-widest text-mute mb-2">{t("ddrv.payoutsTitle")}</div>
        {payouts.length === 0 ? (
          <div className="text-mute text-sm py-2">{t("ddrv.payoutsEmpty")}</div>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto pr-1">
            {payouts.map((p) => (
              <div
                key={p.payout_id}
                id={`payout-row-${p.payout_id}`}
                data-testid={`payout-row-${p.payout_id}`}
                className="flex items-center justify-between gap-2 py-2.5 px-2 rounded-xl border-b border-sand/60 last:border-0"
              >
                <div className="min-w-0">
                  <div className="font-display font-bold text-brand-green">{formatGNF(p.amount)}</div>
                  <div className="text-[11px] text-mute truncate">
                    {operatorLabel(p.operator)} · {t("ddrv.refLabel")} : {p.reference || p.payout_id}
                  </div>
                  {p.express && p.order_id && (
                    <Link
                      to={`/driver?tab=history&highlight=${p.order_id}`}
                      data-testid={`payout-view-order-${p.payout_id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-red underline underline-offset-2 mt-0.5"
                    >
                      {t("ddrv.viewOrder")} #{p.order_id.slice(-6).toUpperCase()}
                    </Link>
                  )}
                </div>
                <div className="text-right shrink-0">
                  {p.express && (
                    <span className="inline-block rounded-full bg-[#FF7900]/15 text-[#C05F00] px-2 py-0.5 text-[10px] font-bold mb-0.5">⚡ {t("ddrv.expressBadge")}</span>
                  )}
                  <div className="text-[11px] text-mute">
                    {p.paid_at ? new Date(p.paid_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StatPill({ label, value, color, testid }) {
  return (
    <div className="bg-white border border-sand rounded-2xl px-4 py-3 flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-full grid place-items-center" style={{ backgroundColor: color + "22", color }}>
        <span className="font-display text-lg font-bold">{value}</span>
      </div>
      <div className="text-xs uppercase tracking-widest text-mute">{label}</div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-xs uppercase tracking-[0.25em] text-mute mb-2 px-1">{children}</h2>;
}

function OrderCard({ order: o, onAction, primary, priority, online, onIncident, share = 0.7 }) {
  const { t } = useTranslation();
  const hasClientGps = o.client_gps?.lat != null && o.client_gps?.lng != null;
  const hasRestoGps = o.restaurant_gps?.lat != null && o.restaurant_gps?.lng != null;
  const clientMapsHref = hasClientGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${o.client_gps.lat},${o.client_gps.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(o.address + ", Conakry")}`;
  const restoMapsHref = hasRestoGps
    ? `https://www.google.com/maps/dir/?api=1&destination=${o.restaurant_gps.lat},${o.restaurant_gps.lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent((o.restaurant_address || o.restaurant_name) + ", Conakry")}`;
  // Avant la prise en charge → itinéraire vers le RESTAURANT (retrait) ; en course → vers le client
  const mapsHref = o.status === "delivering" ? clientMapsHref : restoMapsHref;
  const phoneHref = o.phone ? `tel:${o.phone.replace(/[^+0-9]/g, "")}` : null;
  const isCash = o.payment_method === "cash";
  // Countdown for priority offers
  const [msLeft, setMsLeft] = useState(() => {
    if (!priority || !o.preferred_until) return null;
    return Math.max(0, new Date(o.preferred_until).getTime() - Date.now());
  });
  useEffect(() => {
    if (!priority || !o.preferred_until) return;
    const t = setInterval(() => {
      const left = Math.max(0, new Date(o.preferred_until).getTime() - Date.now());
      setMsLeft(left);
    }, 500);
    return () => clearInterval(t);
  }, [priority, o.preferred_until]);
  const secLeft = msLeft != null ? Math.ceil(msLeft / 1000) : null;
  const borderClass = priority
    ? "border-2 border-brand-red shadow-lg ring-4 ring-brand-red/20"
    : primary ? "border-2 border-brand-red shadow-lg" : "border border-sand";
  return (
    <div className={`bg-white rounded-2xl p-4 ${borderClass}`} data-testid={`driver-order-${o.order_id}`}>
      {priority && secLeft != null && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-xl bg-[#FFF5F6] px-3 py-2 border border-brand-red/20" data-testid={`priority-countdown-${o.order_id}`}>
          <span className="text-xs text-brand-red font-semibold uppercase tracking-widest">{t("ddrv.excl")}</span>
          <span className="font-display text-lg font-bold text-brand-red tabular-nums">{secLeft}s</span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-display text-lg font-semibold">#{o.order_id.slice(-6).toUpperCase()}</div>
          <div className="text-sm text-mute truncate">{o.restaurant_name}</div>
          <div className="text-xs text-mute mt-0.5">
            {o.restaurant_zone || o.zone}
            {o.dispatch_distance_km != null && <span className="ml-2">• {o.dispatch_distance_km} km</span>}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-mute">{t("ddrv.yourGain")}</div>
          <div className="font-display text-lg font-semibold text-brand-green" data-testid={`driver-order-gain-${o.order_id}`}>{formatGNF(Math.round((o.delivery_fee || 0) * share))}</div>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: STATUS_COLOR[o.status] + "22", color: STATUS_COLOR[o.status] }}>
            {t(`status.${o.status}`, STATUS_LABEL[o.status])}
          </span>
        </div>
      </div>

      {isCash && (
        <div className="rounded-xl bg-[#FFF7DB] border border-[#E8D48B] p-3 mb-3 text-sm space-y-1" data-testid={`driver-cash-info-${o.order_id}`}>
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#8A6D00]">{t("ddrv.cashCollect")}</span>
            <b className="font-display">{formatGNF(o.total)}</b>
          </div>
          <div className="flex items-center justify-between text-[#8A6D00]">
            <span>{t("ddrv.remitTo")}</span>
            <b>{formatGNF(Math.max(0, (o.total || 0) - Math.round((o.delivery_fee || 0) * share)))}</b>
          </div>
        </div>
      )}

      <div className="space-y-2 text-sm border-t border-sand pt-3 mb-3">
        <div className="flex items-start gap-2" data-testid={`pickup-address-${o.order_id}`}>
          <Package className="w-4 h-4 mt-0.5 text-[#8A6D00] shrink-0" />
          <span className="break-words min-w-0">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-[#8A6D00]">{t("ddrv.pickupFrom", { name: o.restaurant_name })}</span>
            {o.restaurant_address || o.restaurant_zone || t("ddrv.restoAddrMissing")}
          </span>
        </div>
        <div className="flex items-start gap-2" data-testid={`delivery-address-${o.order_id}`}>
          <MapPin className="w-4 h-4 mt-0.5 text-brand-red shrink-0" />
          <span className="break-words min-w-0">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-brand-red">{t("ddrv.deliverTo")}</span>
            {(o.is_gift ? o.gift_recipient_name : o.client_name) && (
              <span className="block font-semibold" data-testid={`client-name-${o.order_id}`}>
                {o.is_gift ? o.gift_recipient_name : o.client_name}
              </span>
            )}
            {o.address}{o.zone ? ` — ${o.zone}` : ""}
            {hasClientGps && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F0F9F4] text-brand-green px-2 py-0.5 text-[10px] font-bold align-middle" data-testid={`client-gps-badge-${o.order_id}`}>{t("ddrv.clientGps")}</span>}
          </span>
        </div>
        {o.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-green shrink-0" /> {o.phone}</div>}
        <div className="flex items-center gap-2 text-mute"><Clock className="w-4 h-4 shrink-0" /> {new Date(o.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
      </div>

      {o.is_gift && (
        <div className="rounded-xl border border-brand-yellow bg-brand-yellow/10 p-3 mb-3 text-sm" data-testid={`driver-gift-${o.order_id}`}>
          <div className="font-semibold text-xs uppercase tracking-widest mb-1">{t("ddrv.giftHand")}</div>
          <div className="font-semibold">{o.gift_recipient_name}</div>
          {o.gift_recipient_phone && (
            <a href={`tel:${o.gift_recipient_phone.replace(/\s/g, "")}`} className="text-brand-green font-medium">{o.gift_recipient_phone}</a>
          )}
          {o.gift_message && <div className="text-xs text-mute mt-1 italic">« {o.gift_message} »</div>}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <a href={mapsHref} target="_blank" rel="noreferrer" data-testid={`driver-maps-${o.order_id}`} className="rounded-xl border border-sand hover:border-brand-red py-3 text-sm font-semibold text-center inline-flex items-center justify-center gap-1.5">
          <Navigation className="w-4 h-4" /> {o.status === "delivering" ? t("ddrv.routeClient") : t("ddrv.routePickup")}
        </a>
        {phoneHref ? (
          <a href={phoneHref} data-testid={`driver-call-${o.order_id}`} className="rounded-xl border border-sand hover:border-brand-green py-3 text-sm font-semibold text-center inline-flex items-center justify-center gap-1.5">
            <Phone className="w-4 h-4" /> {t("ddrv.call")}
          </a>
        ) : <div />}
      </div>

      <div className="mt-3">
        {o.status === "ready" && (
          <button onClick={() => onAction(o.order_id, "delivering")} data-testid={`driver-pickup-${o.order_id}`} className="w-full rounded-xl bg-brand-yellow text-ink py-4 font-semibold text-base inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            <Package className="w-5 h-5" /> {t("ddrv.btnPickup")}
          </button>
        )}
        {o.status === "delivering" && (
          <>
            <button onClick={() => onAction(o.order_id, "delivered")} data-testid={`driver-delivered-${o.order_id}`} className="w-full rounded-xl bg-brand-green text-white py-4 font-semibold text-base inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
              <CheckCircle2 className="w-5 h-5" /> {t("ddrv.btnDeliver")}
            </button>
            {!online && (
              <div className="mt-2 text-xs text-brand-red text-center">{t("ddrv.warnOnlineGps")}</div>
            )}
            {isCash && (
              <button
                onClick={() => onIncident?.(o.order_id)}
                data-testid={`driver-incident-${o.order_id}`}
                className="mt-2 w-full rounded-xl border border-brand-red/40 text-brand-red hover:bg-brand-red hover:text-white transition-colors py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <AlertOctagon className="w-4 h-4" /> {t("ddrv.btnIncident")}
              </button>
            )}
            {!isCash && (
              <button
                onClick={() => onIncident?.(o.order_id)}
                data-testid={`driver-incident-${o.order_id}`}
                className="mt-2 w-full rounded-xl border border-sand text-mute hover:border-brand-red hover:text-brand-red transition-colors py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                <AlertOctagon className="w-4 h-4" /> {t("ddrv.btnIncident")}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Grayed-out pre-announcement card: order still being prepared, not claimable yet. */
function PreannounceCard({ order: o, share = 0.7 }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const etaMin = o.ready_estimate_at
    ? Math.max(0, Math.round((new Date(o.ready_estimate_at).getTime() - now) / 60000))
    : null;
  return (
    <div className="bg-white/70 border border-dashed border-sand rounded-2xl p-4 opacity-70" data-testid={`driver-preannounce-${o.order_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-base font-semibold text-mute">#{o.order_id.slice(-6).toUpperCase()}</div>
          <div className="text-sm text-mute truncate">{o.restaurant_name}</div>
          <div className="text-xs text-mute mt-0.5">{o.restaurant_zone || o.zone}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-mute">{t("ddrv.yourGain")}</div>
          <div className="font-display text-base font-semibold text-mute">{formatGNF(Math.round((o.delivery_fee || 0) * share))}</div>
          <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: "#E67E2222", color: "#E67E22" }}>
            {t("ddrv.preStatus")}
          </span>
        </div>
      </div>
      <div className="mt-3 rounded-xl bg-elevated px-3 py-2 text-xs text-mute flex items-center gap-2">
        <ChefHat className="w-4 h-4 shrink-0 text-[#E67E22]" />
        {etaMin != null
          ? (etaMin > 0 ? t("ddrv.preReadyIn", { min: etaMin }) : t("ddrv.preSoon"))
          : t("ddrv.prePreparing")}
      </div>
    </div>
  );
}
